import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;
  readonly details: unknown;

  constructor(
    message: string,
    status = 0,
    fields: Record<string, string> = {},
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.fields = fields;
    this.details = details;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & { body?: unknown };

function fieldMap(body: ApiErrorBody): Record<string, string> {
  const result: Record<string, string> = {};
  for (const issue of body.issues ?? []) {
    const path = Array.isArray(issue.path) ? issue.path.join(".") : issue.path;
    if (path) result[path] = issue.message;
  }
  for (const [key, value] of Object.entries(body.fieldErrors ?? {})) {
    result[key] = Array.isArray(value) ? value.join(" ") : value;
  }
  if (body.error && typeof body.error === "object") {
    Object.assign(result, body.error.fields ?? {});
  }
  return result;
}

function errorMessage(body: ApiErrorBody | null, status: number): string {
  if (body?.message) return body.message;
  if (typeof body?.error === "string") return body.error;
  if (body?.error && typeof body.error === "object" && body.error.message)
    return body.error.message;
  if (status === 401)
    return "Oturumunuz sona erdi. Lütfen yeniden giriş yapın.";
  if (status === 403) return "Bu işlem için yetkiniz bulunmuyor.";
  if (status === 404) return "İstenen kayıt bulunamadı.";
  if (status === 409)
    return "Kayıt başka bir işlem nedeniyle değişti. Sayfayı yenileyin.";
  if (status >= 500)
    return "Sunucuda bir sorun oluştu. Lütfen kısa süre sonra yeniden deneyin.";
  return "İşlem tamamlanamadı.";
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  if (!navigator.onLine && options.method && options.method !== "GET") {
    throw new ApiError(
      "Çevrimdışıyken kayıt değişikliği yapılamaz. Bağlantınızı kontrol edin.",
    );
  }

  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers,
      credentials: "same-origin",
      body:
        options.body === undefined
          ? undefined
          : options.body instanceof FormData
            ? options.body
            : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiError(
      "Sunucuya ulaşılamadı. İnternet bağlantınızı kontrol edin.",
    );
  }

  const contentType = response.headers.get("content-type") ?? "";
  const parsed = contentType.includes("application/json")
    ? ((await response.json()) as unknown)
    : await response.text();

  if (!response.ok) {
    const body =
      typeof parsed === "object" && parsed !== null
        ? (parsed as ApiErrorBody)
        : null;
    if (response.status === 401 && path !== "/api/auth/login") {
      window.dispatchEvent(new Event("kompozit:session-expired"));
    }
    throw new ApiError(
      errorMessage(body, response.status),
      response.status,
      body ? fieldMap(body) : {},
      body?.error && typeof body.error === "object" ? body.error.details : undefined,
    );
  }

  if (typeof parsed === "object" && parsed !== null && "data" in parsed) {
    return (parsed as { data: T }).data;
  }
  return parsed as T;
}

export async function downloadApi(
  path: string,
  body: unknown,
  fallbackName: string,
): Promise<void> {
  if (!navigator.onLine)
    throw new ApiError("Dışa aktarma için internet bağlantısı gerekir.");
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/octet-stream",
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let error: ApiErrorBody | null = null;
    try {
      error = (await response.json()) as ApiErrorBody;
    } catch {
      // A binary endpoint may return an empty error response.
    }
    throw new ApiError(
      errorMessage(error, response.status),
      response.status,
      error ? fieldMap(error) : {},
    );
  }
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const simple = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : (simple ?? fallbackName);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function query(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "")
      search.set(key, String(value));
  }
  const output = search.toString();
  return output ? `?${output}` : "";
}
